<template>
  <aside class="jl-filters">
    <div class="jl-filters__header">
      <div class="jl-filters__heading">
        <span class="jl-filters__icon-wrap" aria-hidden="true">
          <v-icon size="18" color="#0054ad">{{ $icons.tune }}</v-icon>
        </span>
        <div>
          <h2 class="jl-filters__title">Filters</h2>
          <p class="jl-filters__subtitle">Refine your job search</p>
        </div>
      </div>
      <button
        v-if="hasSidebarFilters"
        type="button"
        class="jl-filters__clear"
        @click="$emit('clear-filter', 'all')"
      >
        Clear all
      </button>
    </div>

    <div v-if="activeChips.length" class="jl-filters__chips">
      <span
        v-for="chip in activeChips"
        :key="chip.key"
        class="jl-filters__chip"
      >
        {{ chip.label }}
        <button
          type="button"
          class="jl-filters__chip-remove"
          :aria-label="`Remove ${chip.label}`"
          @click="$emit('clear-filter', chip.key)"
        >×</button>
      </span>
    </div>

    <div class="jl-filters__groups">
      <section
        class="jl-filter-section"
        :class="{
          'jl-filter-section--open': openRole,
          'jl-filter-section--active': !!filter.job_role,
        }"
      >
        <button type="button" class="jl-filter-section__trigger" @click="toggleSection('role')">
          <span class="jl-filter-section__label">
            <span class="jl-filter-section__name">Job role</span>
            <span v-if="selectedRoleLabel" class="jl-filter-section__value">{{ selectedRoleLabel }}</span>
          </span>
          <span class="jl-filter-section__chevron" aria-hidden="true"></span>
        </button>
        <div v-show="openRole" class="jl-filter-section__panel">
          <div class="jl-filter-input-wrap">
            <span class="jl-filter-input-wrap__icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="7"/>
                <line x1="16.5" y1="16.5" x2="21" y2="21"/>
              </svg>
            </span>
            <input
              type="text"
              class="jl-filter-input"
              placeholder="Search role..."
              v-model="roleQuery"
              @input="filterRole"
            />
          </div>
          <ul v-if="displayRoles.length" class="jl-filter-list">
            <li
              v-for="role in displayRoles"
              :key="role.value"
              :class="{ 'jl-filter-list__item--selected': filter.job_role === role.value }"
              @click="selectRole(role)"
            >{{ role.text }}</li>
          </ul>
          <p v-else class="jl-filter-empty">No roles found</p>
        </div>
      </section>

      <section
        class="jl-filter-section"
        :class="{
          'jl-filter-section--open': openExp,
          'jl-filter-section--active': !!filter.experience_from,
        }"
      >
        <button type="button" class="jl-filter-section__trigger" @click="toggleSection('exp')">
          <span class="jl-filter-section__label">
            <span class="jl-filter-section__name">Experience</span>
            <span v-if="selectedExpLabel" class="jl-filter-section__value">{{ selectedExpLabel }}</span>
          </span>
          <span class="jl-filter-section__chevron" aria-hidden="true"></span>
        </button>
        <div v-show="openExp" class="jl-filter-section__panel">
          <div class="jl-filter-input-wrap">
            <span class="jl-filter-input-wrap__icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="7"/>
                <line x1="16.5" y1="16.5" x2="21" y2="21"/>
              </svg>
            </span>
            <input
              type="text"
              class="jl-filter-input"
              placeholder="Search experience..."
              v-model="expQuery"
              @input="filterExp"
            />
          </div>
          <ul v-if="displayExp.length" class="jl-filter-list">
            <li
              v-for="item in displayExp"
              :key="item.value"
              :class="{ 'jl-filter-list__item--selected': filter.experience_from === item.value }"
              @click="selectExp(item)"
            >{{ item.text }}</li>
          </ul>
          <p v-else class="jl-filter-empty">No experience levels found</p>
        </div>
      </section>

      <section
        class="jl-filter-section"
        :class="{
          'jl-filter-section--open': openSalary,
          'jl-filter-section--active': !!filter.salary_range_from,
        }"
      >
        <button type="button" class="jl-filter-section__trigger" @click="toggleSection('salary')">
          <span class="jl-filter-section__label">
            <span class="jl-filter-section__name">Salary (monthly)</span>
            <span v-if="filter.salary_range_from" class="jl-filter-section__value">₹{{ formatSalary(filter.salary_range_from) }}+</span>
          </span>
          <span class="jl-filter-section__chevron" aria-hidden="true"></span>
        </button>
        <div v-show="openSalary" class="jl-filter-section__panel">
          <label class="jl-filter-field-label" for="jl-salary-min">Minimum salary</label>
          <div class="jl-filter-salary-row">
            <div class="jl-filter-salary">
              <span class="jl-filter-salary__prefix">₹</span>
              <input
                id="jl-salary-min"
                type="number"
                class="jl-filter-input jl-filter-input--salary"
                placeholder="25000"
                v-model="salaryInput"
                min="0"
                @keyup.enter="applySalary"
              />
            </div>
            <button type="button" class="hp-btn hp-btn--primary jl-filter-apply" @click="applySalary">
              Apply
            </button>
          </div>
        </div>
      </section>
    </div>
  </aside>
</template>

<script>
export default {
  name: 'JobsListFilters',
  props: {
    filter: {
      type: Object,
      required: true,
    },
    jobrole: { type: Array, default: () => [] },
    expOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      openRole: false,
      openExp: false,
      openSalary: false,
      roleQuery: '',
      expQuery: '',
      salaryInput: '',
    };
  },
  computed: {
    selectedRoleLabel() {
      if (!this.filter.job_role) return '';
      const match = this.jobrole.find(role => role.value === this.filter.job_role);
      return match ? match.text : '';
    },
    selectedExpLabel() {
      if (!this.filter.experience_from) return '';
      const match = this.expOptions.find(item => item.value === this.filter.experience_from);
      return match ? match.text : '';
    },
    hasSidebarFilters() {
      return !!(this.filter.job_role || this.filter.experience_from || this.filter.salary_range_from);
    },
    activeChips() {
      const chips = [];
      if (this.selectedRoleLabel) {
        chips.push({ key: 'role', label: this.selectedRoleLabel });
      }
      if (this.selectedExpLabel) {
        chips.push({ key: 'exp', label: this.selectedExpLabel });
      }
      if (this.filter.salary_range_from) {
        chips.push({ key: 'salary', label: `₹${this.formatSalary(this.filter.salary_range_from)}+` });
      }
      return chips;
    },
    displayRoles() {
      const query = this.roleQuery.trim();
      if (!query) return this.jobrole;
      return this.jobrole.filter(role => role.text.match(new RegExp(query, 'i')));
    },
    displayExp() {
      const query = this.expQuery.trim();
      if (!query) return this.expOptions;
      return this.expOptions.filter(item => item.text.match(new RegExp(query, 'i')));
    },
  },
  watch: {
    'filter.salary_range_from': {
      immediate: true,
      handler(val) {
        this.salaryInput = val || '';
      },
    },
  },
  methods: {
    formatSalary(value) {
      const num = Number(value);
      if (Number.isNaN(num)) return value;
      return num.toLocaleString('en-IN');
    },
    toggleSection(section) {
      const next = {
        role: !this.openRole,
        exp: !this.openExp,
        salary: !this.openSalary,
      }[section];

      this.openRole = false;
      this.openExp = false;
      this.openSalary = false;

      if (section === 'role') this.openRole = next;
      if (section === 'exp') this.openExp = next;
      if (section === 'salary') this.openSalary = next;

      if (next && section === 'role') {
        this.roleQuery = '';
      }
      if (next && section === 'exp') {
        this.expQuery = '';
      }
    },
    filterRole() {
      /* roleQuery drives displayRoles computed */
    },
    filterExp() {
      /* expQuery drives displayExp computed */
    },
    selectRole(role) {
      this.$emit('set-role', role);
      this.openRole = false;
    },
    selectExp(item) {
      this.$emit('set-exp', item);
      this.openExp = false;
    },
    applySalary() {
      if (!this.salaryInput) return;
      this.$emit('set-salary', this.salaryInput);
      this.openSalary = false;
    },
  },
};
</script>
